import { Dispatch } from "redux";
import { QueryParamsMap } from "app/RouteDefinition";
import * as api from "app/shared/api";
import { getEarlyBirdOffer } from "app/shared/api";
import { getSelectedDivisionId } from "app/pages/AgentPricing/utils/division";
import { pageLoaded } from "app/pages/EarlyBirdOfferBuilder/actions";
import {
    Action,
    EarlyBirdRuleWithUiId,
} from "app/pages/EarlyBirdOfferBuilder/actionTypes";
import { formatProductId } from "app/pages/EarlyBirdOfferBuilder/productIdHelpers";
import { EarlyBirdOfferDto } from "app/shared/model/EarlyBirdOfferDto";

function addUiIdToCondition<T>(c: T): T & { uiId: string } {
    return { ...c, uiId: crypto.randomUUID() };
}

function toProductIdList(
    format: "Course" | "Accommodation" | "AddOn" | "Transfer",
    ids: any[] | undefined
): string[] {
    if (!Array.isArray(ids)) {
        return [];
    }

    return ids.map((id: any) => {
        if (typeof id !== "string") return formatProductId(format, id);
        const stripped = id.replace(new RegExp(`^(${format}-)+`, "i"), "");
        return `${format}-${stripped}`;
    });
}

function normalizeCondition(c: any): any {
    const out = { ...c };

    if (c.field === "course" && Array.isArray(c.courseIds)) {
        out.courseIds = toProductIdList("Course", c.courseIds);
    }

    if (c.field === "accommodation" && Array.isArray(c.accommodationIds)) {
        out.accommodationIds = toProductIdList("Accommodation", c.accommodationIds);
    }

    if (c.field === "addOn" && Array.isArray(c.addOnIds)) {
        out.addOnIds = toProductIdList("AddOn", c.addOnIds);
    }

    if (c.field === "transfer" && Array.isArray(c.transferIds)) {
        out.transferIds = toProductIdList("Transfer", c.transferIds);
    }

    return out;
}

function mapRuleToWithUiId(rule: {
    name: string;
    earlyBirdEndDate: string;
    include: any[];
    exclude: any[];
}): EarlyBirdRuleWithUiId {
    return {
        name: rule.name,
        earlyBirdEndDate: rule.earlyBirdEndDate,
        include: (rule.include || []).map((c) => addUiIdToCondition(normalizeCondition(c))),
        exclude: (rule.exclude || []).map((c) => addUiIdToCondition(normalizeCondition(c))),
    };
}

function extractAgentIds(offer: EarlyBirdOfferDto): string[] {
    const ids = new Set<string>();

    for (const rule of offer.rules ?? []) {
        for (const condition of [...(rule.include ?? []), ...(rule.exclude ?? [])]) {
            if (condition?.field !== "agent") continue;

            const agents = (condition as any).agents;
            if (!Array.isArray(agents)) continue;

            for (const agent of agents) {
                if (agent?.agentId) {
                    ids.add(String(agent.agentId));
                }
            }
        }
    }

    return [...ids];
}

export function loadPage(
    dispatch: Dispatch<Action>,
    qs: QueryParamsMap
): Promise<void> {
    const yearParam = qs.year;
    const year = yearParam ? parseInt(yearParam, 10) : new Date().getFullYear();

    const divisionId = getSelectedDivisionId();

    if (!divisionId) {
        return Promise.reject(
            new Error("Missing division (select a division first).")
        );
    }

    const offerPromise = getEarlyBirdOffer(divisionId, year);
    const countriesPromise = api.getAllCountries();
    const schoolsPromise = api.getAllSchools();
    const bookingSystemsPromise = api.getAllBookingSystems2();
    const coursesPromise = api.getCourseProducts(divisionId);
    const accommodationsPromise = api.getAccommodationProducts();
    const addOnsPromise = api.getAddonsProducts(divisionId);
    const accommodationRoomsPromise = api.getAccommodationRoomProductsByDivision(divisionId);
    const transfersPromise = api.getTransferProducts(divisionId);
    const addOnTypesPromise = api.getAllAddonTypes();
    const accommodationTypesPromise = api.getAllAccommodationTypes();
    const admissionRegionsPromise = api
        .getRegions(divisionId)
        .then((regions) =>
            [...new Set(regions.map((x) => String(x.name ?? "").trim()).filter(Boolean))]
                .sort((a, b) => a.localeCompare(b))
        );

    const agentsPromise = offerPromise.then((offer) => {
        const agentIds = extractAgentIds(offer);
        if (!agentIds.length) {
            return [];
        }
        return api.getAgentOfficesById(agentIds, false);
    });

    return Promise.all([
        offerPromise,
        countriesPromise,
        schoolsPromise,
        bookingSystemsPromise,
        coursesPromise,
        accommodationsPromise,
        addOnsPromise,
        accommodationRoomsPromise,
        transfersPromise,
        addOnTypesPromise,
        accommodationTypesPromise,
        admissionRegionsPromise,
        agentsPromise,
    ]).then(
        ([
         offer,
         countries,
         schools,
         bookingSystems,
         courses,
         accommodations,
         addOns,
         accommodationRooms,
         transfers,
         addOnTypes,
         accommodationTypes,
         admissionRegions,
         agents,
         ]) => {
            const rules: EarlyBirdRuleWithUiId[] =
                offer.rules.length > 0
                    ? offer.rules.map(mapRuleToWithUiId)
                    : [
                        {
                            name: "",
                            earlyBirdEndDate: "",
                            include: [],
                            exclude: [],
                        },
                    ];

            dispatch(
                pageLoaded(rules, year, divisionId, {
                    countries,
                    schools,
                    bookingSystems,
                    courses,
                    accommodations,
                    addOns,
                    accommodationRooms,
                    transfers,
                    addOnTypes,
                    accommodationTypes,
                    admissionRegions,
                    agents,
                })
            );
        }
    );
}